// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[inert]')) return false;

    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  });
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Clean up any previously set traps.
  removeTrapFocus();

  const resolveFocusable = () => getFocusableElements(container);

  if (!resolveFocusable().length) {
    // If nothing is focusable, just abort—no need to trap.
    return;
  }

  // Keydown handler for cycling focus with Tab and Shift+Tab
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = resolveFocusable();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeEl = document.activeElement;

    // If on the last focusable and tabbing forward, go to first
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first?.focus();
    }
    // If on the first (or the container) and shift-tabbing, go to last
    else if (event.shiftKey && (activeEl === first || activeEl === container)) {
      event.preventDefault();
      last?.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    const focusable = resolveFocusable();
    const first = focusable[0];

    // Release stale traps when the container no longer has a focusable target.
    if (!first || first.closest('[inert]')) {
      removeTrapFocus();
      return;
    }

    // If the newly focused element isn't inside the container, redirect focus back.
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      first.focus();
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it.
  container.focus();
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
/**
 * Resolves the disclosure trigger for an overlay, preferring the activating
 * click target over document.activeElement (VoiceOver often leaves body focused).
 *
 * @param {object} options
 * @param {string} options.id - The overlay id matched by aria-controls.
 * @param {Event} [options.event] - The user event that opened the overlay.
 * @param {HTMLElement} [options.explicitTrigger] - A known trigger element.
 * @returns {HTMLElement | null}
 */
export function resolveDisclosureTrigger({ id, event, explicitTrigger }) {
  if (explicitTrigger instanceof HTMLElement) return explicitTrigger;

  if (event?.target instanceof Element) {
    const fromEvent = event.target.closest(`[aria-controls="${CSS.escape(id)}"]`);
    if (fromEvent instanceof HTMLElement) return fromEvent;
  }

  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    const fromActive = active.closest(`[aria-controls="${CSS.escape(id)}"]`);
    if (fromActive instanceof HTMLElement) return fromActive;
  }

  const trigger = document.querySelector(`[aria-controls="${CSS.escape(id)}"]`);
  return trigger instanceof HTMLElement ? trigger : null;
}

/**
 * Returns focus to a disclosure trigger after an overlay closes.
 *
 * @param {HTMLElement | null} trigger - Stored trigger from open.
 * @param {string} id - Overlay id for aria-controls fallback lookup.
 */
export function restoreDisclosureFocus(trigger, id) {
  const focusTarget =
    trigger && document.contains(trigger)
      ? trigger
      : document.querySelector(`[aria-controls="${CSS.escape(id)}"]`);

  if (!(focusTarget instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    focusTarget.focus({ preventScroll: true });
  });
}

export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
